import { type ReactElement, type ReactNode, createContext, useContext, useEffect, useState } from "react";
import type { AuthenticationResult, EventMessage, IPublicClientApplication } from "@azure/msal-browser";
import { AuthError, EventType, PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import { readConfig } from "../config";
import { buildMsalConfig } from "./msalConfig";

/**
 * The sanitized sign-in failure `SignInView` renders — MSAL's short
 * `errorCode` string only, never the raw `Error`/`AuthError` object, its
 * `errorMessage`, or any token/claim content (ISSUE-329 §Log statements).
 */
const SignInErrorContext = createContext<string | null>(null);

export function useSignInError(): string | null {
  return useContext(SignInErrorContext);
}

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Narrows `EventMessage["payload"]` (a broad union across every MSAL event
 * type) to `AuthenticationResult` for a `LOGIN_SUCCESS` message — the only
 * payload shape MSAL emits for that event type, but the SDK's own type
 * covers every event's payload with one union, so the caller must narrow
 * rather than assert. `accessToken` is present on `AuthenticationResult` and
 * on no other member of the union (request payloads carry only a requested
 * `scopes`/`account`, never an issued token).
 */
function isAuthenticationResult(payload: EventMessage["payload"]): payload is AuthenticationResult {
  return payload !== null && typeof payload === "object" && "accessToken" in payload;
}

/**
 * The only place a `PublicClientApplication` is constructed (ISSUE-329
 * Deliverable C, step 6). `MsalProvider` itself drives `initialize()` and
 * `handleRedirectPromise()` and swallows their rejections internally (see
 * `@azure/msal-react`'s `MsalProvider` source — "Errors should be handled by
 * listening to the LOGIN_FAILURE event"), so a failed/denied sign-in is read
 * back here via MSAL's own event callback instead: every `EventMessage`
 * carries an `error` field regardless of event type.
 *
 * Renders `children` un-wrapped when the runtime configuration is
 * incomplete — building `Configuration` from empty strings and attempting a
 * sign-in against it is never correct (ISSUE-329 §Cross-service JSON
 * contract). `RequireAuth` renders the configuration-error screen itself in
 * that case instead.
 */
export function AuthProvider({ children }: AuthProviderProps): ReactElement {
  const config = readConfig();
  const [instance] = useState<IPublicClientApplication | null>(() =>
    config.ok ? new PublicClientApplication(buildMsalConfig(config.config)) : null,
  );
  const [signInError, setSignInError] = useState<string | null>(null);

  useEffect(() => {
    if (instance === null) {
      return;
    }
    const callbackId = instance.addEventCallback((message: EventMessage) => {
      // Sets MSAL's active account on every successful sign-in (MSAL's
      // documented multi-account pattern — LOGIN_SUCCESS's payload is the
      // AuthenticationResult carrying the account that just signed in).
      // Without this, `instance.getActiveAccount()` stays unset and a second
      // operator signing in under a different account in the same tab would
      // otherwise be resolved via `accounts[0]` — the first, not latest,
      // account in MSAL's cache — mis-attributing that operator's API calls
      // (security-review finding, ISSUE-329 Deliverable C).
      if (
        message.eventType === EventType.LOGIN_SUCCESS &&
        isAuthenticationResult(message.payload) &&
        message.payload.account !== null
      ) {
        instance.setActiveAccount(message.payload.account);
      }
      if (message.error === null) {
        return;
      }
      setSignInError(message.error instanceof AuthError ? message.error.errorCode : "unknown_error");
    });
    return (): void => {
      if (callbackId !== null) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance]);

  if (instance === null) {
    return <>{children}</>;
  }

  return (
    <SignInErrorContext.Provider value={signInError}>
      <MsalProvider instance={instance}>{children}</MsalProvider>
    </SignInErrorContext.Provider>
  );
}
